import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import { requestPublicProviderSettings } from '@/services/public-request';
import type { ProviderTermsSettings, UpdateProviderTermsInput } from '@/types/provider-terms';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

function normalizeTermsUrl(value: unknown): string | null {
  const raw = readString(value)?.trim() ?? '';
  if (raw.length === 0) {
    return null;
  }
  return raw;
}

function normalizeSettings(payload: ApiPayload | undefined): ProviderTermsSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});

  return {
    termsUrl: normalizeTermsUrl(record?.terms_url ?? record?.termsUrl),
  };
}

export class ProviderTermsRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderTerms(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderTermsRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderTermsRequestError('Provider terms request failed', undefined, error);
  }
}

async function requestPublicTerms(
  path: string,
  options: { method?: 'GET'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestPublicProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderTermsRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderTermsRequestError('Provider terms request failed', undefined, error);
  }
}

class ProviderTermsService {
  async getSettings(): Promise<ProviderTermsSettings> {
    const response = await requestProviderTerms('/api/v1/provider/settings/terms');
    return normalizeSettings(response);
  }

  async getPublicTermsUrl(): Promise<string | null> {
    try {
      const response = await requestPublicTerms('/api/v1/public/terms');
      const settings = normalizeSettings(response);
      return settings.termsUrl;
    } catch (error) {
      if (error instanceof ProviderTermsRequestError) {
        const status = error.status ?? 0;
        if (status === 401 || status === 403 || status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  async updateSettings(input: UpdateProviderTermsInput): Promise<ProviderTermsSettings> {
    const response = await requestProviderTerms('/api/v1/provider/settings/terms', {
      method: 'PUT',
      body: { terms_url: input.termsUrl },
    });
    return normalizeSettings(response);
  }
}

export const providerTermsService = new ProviderTermsService();

export { normalizeTermsUrl as normalizeProviderTermsUrl };
